import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect, { type ObjectSelectValue } from "@components/ObjectSelect";
import { useObjects } from "@features/devices/api";
import {
  type DirectoryGroup,
  type DirectoryGroupFilters,
  useAdminGroups,
  useCreateAdminGroup,
  useImportOracleGroups,
} from "@features/groups/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import DrawerForm from "@shared/ui/drawer-form";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStandardPagination } from "@shared/useStandardPagination";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

interface CreateGroupFormValues {
  id: string;
  object: ObjectSelectValue;
  level: number;
  name?: string;
}

const generateGroupId = () =>
  globalThis.crypto?.randomUUID?.() ?? `group-${Math.random().toString(36).slice(2, 10)}`;

const AdminGroupsPage = () => {
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const importGroups = useImportOracleGroups();
  const createGroup = useCreateAdminGroup();
  const [nameFilter, setNameFilter] = useState("");
  const [objectFilter, setObjectFilter] = useState<ObjectSelectValue>();
  const [levelFilter, setLevelFilter] = useState<number | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [createSeedId, setCreateSeedId] = useState(() => generateGroupId());

  const filterParams = useMemo<DirectoryGroupFilters>(
    () => ({
      name: nameFilter.trim() || undefined,
      object: objectFilter,
      level: levelFilter,
    }),
    [nameFilter, objectFilter, levelFilter],
  );

  const groupsQuery = useAdminGroups(filterParams);
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];

  const groups = groupsQuery.data ?? [];
  const pagination = useStandardPagination(groups.length);

  const columns: ColumnsType<DirectoryGroup> = useMemo(
    () => [
      {
        title: "Название",
        dataIndex: "name",
        render: (value: string) => value || "—",
      },
      {
        title: "Объект",
        dataIndex: "object_id",
        render: (_value, record) => `№${record.object_id} (регион ${record.region_id})`,
      },
      {
        title: "Уровень",
        dataIndex: "level",
      },
      {
        title: "Тип учёта",
        dataIndex: "measurement_type",
        render: (value?: number) => value ?? "—",
      },
      {
        title: "Связанные счётчики",
        dataIndex: "channels",
        render: (_value, record) => record.channels?.length ?? 0,
      },
      {
        title: "Действия",
        dataIndex: "actions",
        width: 140,
        render: (_value, record) => (
          <Button size="small" onClick={() => navigate(`/admin/groups/${record.id}`)}>
            Настроить
          </Button>
        ),
      },
    ],
    [navigate],
  );

  const handleImport = async () => {
    try {
      const result = await importGroups.mutateAsync();
      message.success(
        `Импорт завершён: добавлено ${result.groups_inserted} групп и ${result.channels_inserted} связей.`,
      );
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const resetFilters = () => {
    setNameFilter("");
    setObjectFilter(undefined);
    setLevelFilter(undefined);
  };

  const handleOpenCreate = () => {
    setCreateSeedId(generateGroupId());
    setCreateOpen(true);
  };

  const handleCreate = async (values: CreateGroupFormValues) => {
    try {
      const payload: DirectoryGroup = {
        id: values.id.trim(),
        region_id: values.object.regionId,
        object_id: values.object.objectId,
        level: values.level ?? 1,
        name: values.name?.trim() || `Группа ${values.id.trim()}`,
        channels: [],
        members: [],
      };
      await createGroup.mutateAsync({ data: payload });
      message.success("Группа создана");
      setCreateOpen(false);
      navigate(`/admin/groups/${payload.id}`);
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  return (
    <PageLayoutWithHelp
      title="Группы счётчиков"
      extra={
        <Space>
          <Button type="primary" onClick={handleOpenCreate}>
            Создать группу
          </Button>
          <Button onClick={() => navigate("/admin/groups/new")}>Расширенная форма</Button>
        </Space>
      }
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">Управление группами 1 и 2 уровней.</Typography.Text>

        <Alert
          type="warning"
          showIcon
          title="Импорт групп из БД"
          description="Импорт переносит отсутствующие группы и их связи. Повторный запуск безопасен — существующие записи не изменяются."
        />
        <Card>
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Text>
              После миграции редактирование выполняется только в системе. БД используется только как
              источник первичных данных для импорта.
            </Typography.Text>
            <Popconfirm
              title="Импортировать группы из БД?"
              description="Будут добавлены только новые группы и связи со счётчиками."
              onConfirm={handleImport}
            >
              <Button type="primary" loading={importGroups.isPending}>
                Импорт из БД
              </Button>
            </Popconfirm>
          </Space>
        </Card>

        <FilterBar>
          <FilterRow style={{ width: "100%" }}>
            <Input
              allowClear
              placeholder="Название группы"
              value={nameFilter}
              onChange={(event) => setNameFilter(event.target.value)}
              style={{ minWidth: 220, flex: 1 }}
            />
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={objectFilter}
              onChange={setObjectFilter}
              placeholder="Объект"
              allowClear
              style={{ minWidth: 320, flex: 1 }}
            />
            <Select<number>
              allowClear
              placeholder="Уровень"
              value={levelFilter}
              onChange={setLevelFilter}
              style={{ minWidth: 160 }}
              options={[
                { label: "Уровень 1", value: 1 },
                { label: "Уровень 2", value: 2 },
              ]}
            />
            <Button onClick={resetFilters}>Сбросить</Button>
          </FilterRow>
        </FilterBar>

        <EnterpriseTable
          rowKey="id"
          dataSource={groups}
          columns={columns}
          loading={groupsQuery.isLoading}
          pagination={pagination}
          size="small"
          toolbar={{ reload: true, onReload: () => groupsQuery.refetch() }}
        />

        <DrawerForm<CreateGroupFormValues>
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          title="Новая группа"
          size={520}
          onSubmit={handleCreate}
          submitText="Создать"
          cancelText="Отмена"
          showReset
          loading={createGroup.isPending}
          copyUrl={`/admin/groups/${encodeURIComponent(createSeedId)}`}
          initialValues={{
            id: createSeedId,
            level: 1,
          }}
        >
          <Form.Item name="id" label="ID" tooltip="ID группы используется в ссылках">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="object"
            label="Объект"
            rules={[{ required: true, message: "Выберите объект" }]}
          >
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              placeholder="Объект"
              allowClear
              style={{ minWidth: 320 }}
            />
          </Form.Item>
          <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              name="level"
              label="Уровень"
              rules={[{ required: true, message: "Укажите уровень" }]}
              style={{ width: 200 }}
            >
              <Select<number>
                options={[
                  { label: "1 уровень", value: 1 },
                  { label: "2 уровень", value: 2 },
                ]}
              />
            </Form.Item>
            <Form.Item name="name" label="Название" style={{ flex: 1, minWidth: 240 }}>
              <Input placeholder="Название группы" />
            </Form.Item>
          </Space>
        </DrawerForm>
      </Space>
    </PageLayoutWithHelp>
  );
};

export default AdminGroupsPage;
